import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../campaignSetupAgent.enums';

// Tool schema - accepts nodeId to get card details
const getCardDetailsToolSchema = z.object({
  nodeIds: z.array(z.number()).describe('The IDs of the cards to get details for'),
});

function getCardDetailsTool(store: CLStore) {
  async function execute({
    input: {nodeIds},
  }: ToolExecuteParams<typeof getCardDetailsToolSchema>): Promise<ToolResult> {
    try {
      const state = store.getState();
      const {campaignLogic} = state.campaignPage;

      const nodes = campaignLogic.nodes[CampaignTreeType.Campaign];

      // Find the existing node from shared campaigns
      const existingNodes = nodeIds.map((nodeId) => nodes[nodeId]).filter(Boolean);

      const cardsResponse = existingNodes.map((node) => {
        return {
          ...node,
          inputs: node.inputs.map((input) => ({
            nodeId: input.nodeId,
            ouputIndex: input.outputIdx,
          })),
          outputs: node.outputs.map((output) => ({
            nodeId: output.nodeId,
            id: output.id,
            name: output.name,
            type: output.type,
          })),
        };
      });

      const missingNodesMessage =
        existingNodes.length !== nodeIds.length
          ? `Some nodes were not found: ${JSON.stringify(
              nodeIds.filter((nodeId) => !existingNodes.some((node) => node.id === nodeId)),
            )}`
          : '';

      return toolResult(
        `Retrieved details for nodes: ${JSON.stringify(cardsResponse)} ${missingNodesMessage}`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      return toolResult(`Error retrieving card details: ${errorMessage}`);
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.GetCardDetails,
    displayName: 'Retrieving Card Details',
    description: 'Retrieves full details of a card by its ID.',
    execute,
    inputSchema: getCardDetailsToolSchema,
  });
}

export default getCardDetailsTool;
